import { createSlice, createSelector } from '@reduxjs/toolkit';
import { LatLng } from 'leaflet';
import { useQuery } from '@tanstack/react-query';

import store, { RootState, SpacePositionType, AppDispatch, useAppSelector, useAppDispatch } from '../index';
import { activeImageSelector, spacePositionsSelector } from './images';
import { spacesSelector } from './spaces';

export type RouteMode = 'walking' | 'driving';
export type LocationPoint = {
    id: string;
    name: string;
    coordinates: LatLng;
    type: 'space' | 'current';
};

export type SerializableGeolocationPosition = {
  coords: {
    latitude: number;
    longitude: number;
    altitude: number | null;
    accuracy: number;
    altitudeAccuracy: number | null;
    heading: number | null;
    speed: number | null;
  };
  timestamp: number;
};

export function serializeGeolocationPosition(position: GeolocationPosition): SerializableGeolocationPosition {
  return {
    coords: {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      altitude: position.coords.altitude,
      accuracy: position.coords.accuracy,
      altitudeAccuracy: position.coords.altitudeAccuracy,
      heading: position.coords.heading,
      speed: position.coords.speed,
    },
    timestamp: position.timestamp,
  };
}

// Routing types
export interface RouteCoordinate {
  lat: number;
  lng: number;
}

export interface RouteResponse {
  routes: Array<{
    geometry: {
      coordinates: Array<[number, number]>; // [lng, lat]
    };
    duration: number;
    distance: number;
    weight_name: string;
    weight: number;
    legs: Array<{
      steps: Array<{
        geometry: { coordinates: Array<[number, number]> };
        maneuver: {
          instruction: string;
          type: string;
          modifier?: string;
          location: [number, number];
        };
        mode: string;
        distance: number;
        duration: number;
        name: string;
      }>;
      distance: number;
      duration: number;
    }>;
  }>;
  waypoints: Array<{
    hint: string;
    distance: number;
    name: string;
    location: [number, number]; // [lng, lat]
  }>;
  code: string;
  uuid?: string;
}

export const wayfingindSlice = createSlice({
  name: 'wayfinding',
  initialState: {
    isSelectingWaypoints: false,
    selectedWaypointIds: [] as string[],
    currentRoute: null as RouteResponse | null,
    routeMode: 'walking' as 'walking' | 'driving',
    isCalculatingRoute: false,
    routeError: null as string | null,
    showRouteSidebar: false,
    showCurrentLocation: false,
    isTrackingLocation: false,
    currentLocation: null as SerializableGeolocationPosition | null,
  },
  reducers: {
    setIsSelectingWaypoints: (state, { payload }) => ({
      ...state,
      isSelectingWaypoints: payload,
    }),
    setSelectedWaypointIds: (state, { payload }: { payload: string[] }) => ({
      ...state,
      selectedWaypointIds: payload,
    }),
    toggleSelectedWaypoint: (state, { payload }: { payload: string }) => ({
      ...state,
      selectedWaypointIds: state.selectedWaypointIds.includes(payload)
        ? state.selectedWaypointIds.filter((id) => id !== payload)
        : [...state.selectedWaypointIds, payload],
    }),
    selectAllWaypoints: (state, { payload }: { payload: string[] }) => ({
      ...state,
      selectedWaypointIds: payload,
    }),
    clearSelectedWaypoints: (state) => ({
      ...state,
      selectedWaypointIds: [],
    }),
    setCurrentRoute: (state, { payload }: { payload: RouteResponse | null }) => ({
      ...state,
      currentRoute: payload,
    }),
    setCurrentRouteMode: (state, { payload }: { payload: 'walking' | 'driving' }) => ({
      ...state,
      routeMode: payload,
    }),
    setIsCalculatingRoute: (state, { payload }: { payload: boolean }) => ({
      ...state,
      isCalculatingRoute: payload,
    }),
    setRouteError: (state, { payload }: { payload: string | null }) => ({
      ...state,
      routeError: payload,
    }),
    setShowRouteSidebar: (state, { payload }: { payload: boolean }) => ({
      ...state,
      showRouteSidebar: payload,
    }),
    setShowCurrentLocation: (state, { payload }: { payload: boolean }) => ({
      ...state,
      showCurrentLocation: payload,
    }),
    setIsTrackingLocation: (state, { payload }: { payload: boolean }) => ({
      ...state,
      isTrackingLocation: payload,
    }),
    setCurrentLocation: (state, { payload }: { payload: SerializableGeolocationPosition | null }) => ({
      ...state,
      currentLocation: payload,
    }),
  },
});

export const {
  setIsSelectingWaypoints,
  setSelectedWaypointIds,
  toggleSelectedWaypoint,
  setCurrentRoute,
  setCurrentRouteMode,
  setIsCalculatingRoute,
  setRouteError,
  setShowRouteSidebar,
  setShowCurrentLocation,
  setIsTrackingLocation,
  setCurrentLocation,
} = wayfingindSlice.actions;

export const isSelectingWaypointsSelector = (state: RootState) => state.wayfinding.isSelectingWaypoints;

export const selectedWaypointIdsSelector = (state: RootState) => state.wayfinding.selectedWaypointIds;
export const currentRouteSelector = (state: RootState) => state.wayfinding.currentRoute;
export const routeModeSelector = (state: RootState) => state.wayfinding.routeMode;
export const routeErrorSelector = (state: RootState) => state.wayfinding.routeError;
export const showCurrentLocationSelector = (state: RootState) => state.wayfinding.showCurrentLocation;
export const isTrackingLocationSelector = (state: RootState) => state.wayfinding.isTrackingLocation;
export const currentLocationSelector = (state: RootState) => state.wayfinding.currentLocation;

// Mapbox helpers as selectors
export const isMapboxActiveSelector = createSelector(
  activeImageSelector,
  (activeImage) => {
    return activeImage && 'provider' in activeImage && activeImage?.provider === 'mapbox';
  }
);

export const mapboxTokenSelector = createSelector(activeImageSelector, (layer) => {
  if (!layer || !('tiles' in layer)) return null as string | null;
  const tilesUrl = (layer as any).tiles as string | undefined;
  if (!tilesUrl) return null;
  const match = tilesUrl.match(/access_token=([^&]+)/);
  return match ? match[1] : null;
});

export const showRouteSidebarSelector = (state: RootState) => state.wayfinding.showRouteSidebar;

/**
 * Calculate route between waypoints using MapBox Directions API
 * @param waypoints - Array of waypoint coordinates
 * @param accessToken - MapBox access token
 * @param profile - Routing profile (walking, driving)
 * @returns Promise with route data or error
 */
export async function calculateRoute(
  waypoints: Omit<SpacePositionType, 'layerId'>[],
  accessToken: string,
  profile: 'walking' | 'driving'
): Promise<RouteResponse> {
  if (waypoints.length < 2) {
    throw new Error('At least 2 waypoints are required for route calculation');
  }

  // Convert coordinates to MapBox format (lng,lat)
  // Note: SpacePositionType uses x=lat, y=lng, so we swap them for Mapbox
  const coordinates = waypoints
    .map((wp) => `${wp.y},${wp.x}`)
    .join(';');

  const url = `https://api.mapbox.com/directions/v5/mapbox/${profile}/${coordinates}` +
    `?access_token=${accessToken}` +
    '&geometries=geojson' +
    '&overview=full' +
    '&steps=true' +
    '&banner_instructions=true';

  try {
    const response = await fetch(url);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (data.code !== 'Ok') {
      throw new Error(data.message || `Route calculation failed: ${data.code}`);
    }

    return data as RouteResponse;
  } catch (error) {
    console.error('MapBox Directions API error:', error);
    throw error;
  }
}

// Formatting helpers
export const convertRouteToLeafletCoords = (route: RouteResponse['routes'][0]): [number, number][] =>
  route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);

export const formatRouteDuration = (durationSeconds: number): string => {
  const minutes = Math.round(durationSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return hours > 0 ? `${hours}h ${remaining}m` : `${minutes}m`;
};

export const formatRouteDistance = (distanceMeters: number): string =>
  distanceMeters >= 1000 ? `${(distanceMeters / 1000).toFixed(1)}km` : `${Math.round(distanceMeters)}m`;

export const selectedSpacesSelector = createSelector(
  spacesSelector,
  selectedWaypointIdsSelector,
  (spaces, selectedWaypointIds) => spaces.filter((space) => selectedWaypointIds.includes(space.id)),
);

export const selectedSpacePositionsSelector = createSelector(
  selectedSpacesSelector,
  spacePositionsSelector,
  (selectedSpaces, spacePositions) => {
    return Object.fromEntries(
      selectedSpaces.map((space) => [
        space.id,
        spacePositions[space.id],
      ]),
    );
  },
);

// Selectors for route calculation
export const routeWaypointsSelector = createSelector(
  selectedSpacePositionsSelector,
  selectedWaypointIdsSelector,
  currentLocationSelector,
  (spacePositionsMap, selectedWaypointIds, currentLocation) => {
    const waypoints: Array<{ x: number; y: number }> = [];

    // Add waypoints in the order they appear in selectedWaypointIds
    selectedWaypointIds.forEach((id) => {
      if (id === 'current' && currentLocation) {
        waypoints.push({
          x: currentLocation.coords.latitude,
          y: currentLocation.coords.longitude,
        });
      } else if (spacePositionsMap[id]) {
        waypoints.push({
          x: spacePositionsMap[id].x,
          y: spacePositionsMap[id].y,
        });
      }
    });

    return waypoints;
  }
);

export const routeQueryKeySelector = createSelector(
  routeWaypointsSelector,
  selectedWaypointIdsSelector,
  routeModeSelector,
  mapboxTokenSelector,
  (waypoints, selectedWaypointIds, routeMode, token) => {
    const waypointsKey = waypoints.map((wp) => `${wp.x},${wp.y}`).join(';');
    return [
      'route',
      selectedWaypointIds.join(','),
      waypointsKey,
      routeMode,
      token,
    ];
  }
);

export const routeCalculationEnabledSelector = createSelector(
  routeWaypointsSelector,
  mapboxTokenSelector,
  (waypoints, token) => waypoints.length >= 2 && !!token
);

// Query function factory for react-query
export function createRouteQueryFn(dispatch: AppDispatch, getState: () => RootState) {
  return async (): Promise<RouteResponse> => {
    const state = getState();
    const waypoints = routeWaypointsSelector(state);
    const routeMode = routeModeSelector(state);
    const token = mapboxTokenSelector(state);

    if (!token) {
      const error = new Error('MapBox access token not found');
      dispatch(setRouteError(error.message));
      throw error;
    }

    try {
      const route = await calculateRoute(waypoints, token, routeMode);

      dispatch(setCurrentRoute(route));
      dispatch(setRouteError(null));
      dispatch(setShowRouteSidebar(true));

      return route;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to calculate route';
      dispatch(setRouteError(errorMessage));
      throw error;
    }
  };
}

export const availablePointsSelector = createSelector(
  currentLocationSelector,
  spacesSelector,
  spacePositionsSelector,
  (currentLocation, spaces, spacePositions) => [
    ...(currentLocation
      ? [{
        id: 'current',
        name: 'Current Location',
        coordinates: {
          lat: currentLocation.coords.latitude,
          lng: currentLocation.coords.longitude
        },
        type: 'current',
      }]
      : []
    ),
    ...spaces.map((space) => {
      const position = spacePositions[space.id];
      if (!position) return undefined;
      return {
        id: space.id,
        name: space.name,
        coordinates: {
          lat: position.x,
          lng: position.y
        },
        type: 'space',
      };
    }).filter(Boolean)
  ] as LocationPoint[]
);

export const routeStepsSelector = createSelector(
  currentRouteSelector,
  (currentRoute) => {
    if (!currentRoute?.routes?.[0]?.legs) return [];
    return currentRoute.routes[0].legs.flatMap((leg) => leg.steps || []);
  }
);

export const routeSummarySelector = createSelector(
  currentRouteSelector,
  (currentRoute) => {
    if (!currentRoute?.routes[0]) return null;
    return {
      duration: currentRoute.routes[0].duration,
      distance: currentRoute.routes[0].distance,
      waypoints: currentRoute.waypoints.length,
    };
  }
);

export const routeCoordinatesSelector = createSelector(
  currentRouteSelector,
  (currentRoute): [number, number][] | null => {
    if (!currentRoute?.routes?.[0]) return null;
    return convertRouteToLeafletCoords(currentRoute.routes[0]);
  }
);

export function useRouteCalculation() {
  const dispatch = useAppDispatch();
  const queryKey = useAppSelector(routeQueryKeySelector);
  const enabled = useAppSelector(routeCalculationEnabledSelector);

  const query = useQuery({
    queryKey,
    queryFn: createRouteQueryFn(dispatch, () => store.getState()),
    enabled,
    retry: false,
    staleTime: 0,
    refetchOnMount: true,
  });

  return {
    ...query,
    isCalculatingRoute: query.isLoading || query.isFetching,
    calculateRoute: query.refetch,
  };
}

